import { isEmpty } from 'lodash-es';
import {
  Navigate,
  Outlet,
  generatePath,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import {
  AccessTypeEnum,
  SessionContext,
  useSession,
} from '@/providers/SessionProvider/SessionProvider';
import useGetClientId from '@/providers/UserPermissionsProvider/hooks/useGetClientId';

import { matchPath } from './utils';

const RESTRICTED_ROUTES = [
  '/:clientId/organizations',
  '/:clientId/organizations/:organizationId/:organizationTab?',
  '/:clientId/organizations/:organizationId/accounts/:accountId/:accountTab?',
  '/:clientId/campaigns/:campaignId/*',
  '/:clientId/creatives/:creativeId/*',
  '/:clientId/analytics/campaigns-v2/*',
  '/:clientId/analytics/content/*',
  '/:clientId/analytics/audiences/*',
  '/:clientId/analytics/creatives-v2/*',
  '/:clientId/analytics/subject-lines-v2/*',
  '/:clientId/analytics/segments-v2/*',
  '/:clientId/analytics/experiments-v2/*',
  '/:clientId/contacts/*',
  '/:clientId/data/connections/*',
  '/:clientId/data/tables/*',
  '/:clientId/data/jobs/*',
  '/:clientId/brand-kit/*',
] as const;

export type RestrictedRoutes = (typeof RESTRICTED_ROUTES)[number];

type OrganizationUserParam = {
  accounts: SessionContext['currentUserAccounts'];
  organizationId: string | undefined;
  isOrganizationOwner: boolean;
  isOrganizationAdmin: boolean;
  isSuperuser: boolean;
};

const getRestrictedRoute = (
  orgUser: OrganizationUserParam,
  routeParams: ReturnType<typeof matchPath>,
  state: ReturnType<typeof useLocation>['state'],
) => {
  const {
    organizationId,
    isOrganizationOwner,
    isOrganizationAdmin,
    isSuperuser,
  } = orgUser;
  const { params, pattern: currentRoutePattern } = routeParams ?? {};

  const canAccessOrganization =
    organizationId === params?.organizationId &&
    (isOrganizationOwner || isOrganizationAdmin);

  const isSwitchingAccount = state?.isSwitchingAccount;

  // Map the restricted routes and their respective access criteria
  const RestrictedRoutesMap: Readonly<
    Record<RestrictedRoutes, { canAccess: boolean; destination?: string }>
  > = {
    '/:clientId/organizations': { canAccess: isSuperuser },
    '/:clientId/organizations/:organizationId/:organizationTab?': {
      canAccess: isSuperuser || canAccessOrganization,
    },
    '/:clientId/organizations/:organizationId/accounts/:accountId/:accountTab?':
      {
        canAccess: !isSwitchingAccount,
        destination: '/:clientId/campaigns',
      },
    '/:clientId/campaigns/:campaignId/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/campaigns',
    },
    '/:clientId/creatives/:creativeId/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/creatives',
    },
    '/:clientId/analytics/campaigns-v2/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/analytics/campaigns-v2',
    },
    '/:clientId/analytics/content/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/analytics/content',
    },
    '/:clientId/analytics/audiences/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/analytics/audiences',
    },
    '/:clientId/analytics/creatives-v2/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/analytics/creatives-v2',
    },
    '/:clientId/analytics/subject-lines-v2/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/analytics/subject-lines-v2',
    },
    '/:clientId/analytics/segments-v2/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/analytics/segments-v2',
    },
    '/:clientId/analytics/experiments-v2/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/analytics/experiments-v2',
    },
    '/:clientId/contacts/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/contacts/audiences-beta',
    },
    '/:clientId/data/connections/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/data/connections',
    },
    '/:clientId/data/tables/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/data/tables',
    },
    '/:clientId/data/jobs/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/data/jobs',
    },
    '/:clientId/brand-kit/*': {
      canAccess: !isSwitchingAccount,
      destination: '/:clientId/brand-kit/guidelines',
    },
  };

  return currentRoutePattern
    ? (RestrictedRoutesMap[currentRoutePattern] ?? { canAccess: true })
    : { canAccess: true };
};

const RouteMiddleware = () => {
  const {
    accessType,
    isSuperuser,
    organizationId,
    isOrganizationOwner,
    isOrganizationAdmin,
    currentUserAccounts,
    clients,
  } = useSession();
  const clientId = useGetClientId();
  const [searchParams] = useSearchParams();
  const { pathname, state } = useLocation();

  // Check if the user has permission to access the current route
  const hasRestrictedAccessType = accessType !== AccessTypeEnum.Active;
  const routeMatch = matchPath(RESTRICTED_ROUTES, pathname);
  const orgUser: OrganizationUserParam = {
    organizationId,
    isSuperuser,
    isOrganizationOwner,
    isOrganizationAdmin,
    accounts: currentUserAccounts,
  };
  const route = getRestrictedRoute(orgUser, routeMatch, state);
  const isAccessRestricted = hasRestrictedAccessType || !route.canAccess;

  if (!route.canAccess && route.destination) {
    return (
      <Navigate to={generatePath(route.destination, { clientId })} replace />
    );
  }

  // If the user does not have access to given route, redirect to the restricted page
  if (isAccessRestricted && !searchParams.has('type')) {
    return (
      <Navigate
        to={`/access/restricted?type=${hasRestrictedAccessType ? accessType : AccessTypeEnum.Access_Denied}`}
        replace
      />
    );
  }

  /**
   * This may be reached mainly when running it locally, when the back-end may be somehow off
   * and the available clients - CurrentSession query - fetch fails.
   */
  if (
    typeof isAccessRestricted === 'boolean' &&
    !isAccessRestricted &&
    isEmpty(clients)
  ) {
    const clientsNotFoundError = new Error(
      'Oops! There seems to be a problem accessing the accounts available to your user. You can:',
    );
    clientsNotFoundError.name = 'ClientsNotFoundError';
    throw clientsNotFoundError;
  }

  // If the current route is not restricted, render the child components
  return <Outlet context={{ clientId }} />;
};

export default RouteMiddleware;
